/**
 * Memo Types
 *
 * Type definitions for family memos/notes.
 * Memos are quick notes not tied to specific calendar dates.
 *
 * @module features/family-calendar/types/memo.types
 */

// ============================================
// Core Memo Types
// ============================================

/**
 * Memo entity for quick family notes.
 * Unlike events, memos are not tied to specific dates.
 */
export interface Memo {
  /** Unique identifier */
  readonly id: string;

  /** Memo title */
  readonly title: string;

  /** Memo content (can be multi-line) */
  readonly content: string;

  /** Calendar this memo belongs to */
  readonly calendarId: string;

  /** Author's member ID */
  readonly authorId: string;

  /** Color for the memo card (seed id or configurable colour id) */
  readonly color: MemoColorRef;

  /** Whether memo is pinned to top */
  readonly isPinned: boolean;

  /** Record creation timestamp */
  readonly createdAt: string;

  /** Record last update timestamp */
  readonly updatedAt: string;
}

// ============================================
// Enum Types
// ============================================

/**
 * Available memo colors
 */
export type MemoColor =
  | 'yellow'
  | 'blue'
  | 'green'
  | 'pink'
  | 'purple'
  | 'orange';

/**
 * A memo colour reference. Either one of the seed {@link MemoColor} ids (which
 * render via {@link MEMO_COLOR_CONFIG}) or any configurable colour id from the
 * Colors store. The `string & {}` keeps autocomplete for the known set while
 * still allowing custom ids.
 */
export type MemoColorRef = MemoColor | (string & {});

/**
 * All memo color options
 */
export const ALL_MEMO_COLORS: readonly MemoColor[] = [
  'yellow',
  'blue',
  'green',
  'pink',
  'purple',
  'orange',
] as const;

/**
 * Memo color configuration
 */
export const MEMO_COLOR_CONFIG: Readonly<
  Record<
    MemoColor,
    {
      bg: string;
      bgHover: string;
      border: string;
      label: string;
    }
  >
> = {
  yellow: {
    bg: 'bg-yellow-100 dark:bg-yellow-900/30',
    bgHover: 'hover:bg-yellow-200 dark:hover:bg-yellow-900/50',
    border: 'border-yellow-300 dark:border-yellow-700',
    label: 'Жълто',
  },
  blue: {
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    bgHover: 'hover:bg-blue-200 dark:hover:bg-blue-900/50',
    border: 'border-blue-300 dark:border-blue-700',
    label: 'Синьо',
  },
  green: {
    bg: 'bg-green-100 dark:bg-green-900/30',
    bgHover: 'hover:bg-green-200 dark:hover:bg-green-900/50',
    border: 'border-green-300 dark:border-green-700',
    label: 'Зелено',
  },
  pink: {
    bg: 'bg-pink-100 dark:bg-pink-900/30',
    bgHover: 'hover:bg-pink-200 dark:hover:bg-pink-900/50',
    border: 'border-pink-300 dark:border-pink-700',
    label: 'Розово',
  },
  purple: {
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    bgHover: 'hover:bg-purple-200 dark:hover:bg-purple-900/50',
    border: 'border-purple-300 dark:border-purple-700',
    label: 'Лилаво',
  },
  orange: {
    bg: 'bg-orange-100 dark:bg-orange-900/30',
    bgHover: 'hover:bg-orange-200 dark:hover:bg-orange-900/50',
    border: 'border-orange-300 dark:border-orange-700',
    label: 'Оранжево',
  },
} as const;

// ============================================
// DTO Types
// ============================================

/**
 * Data for creating a new memo
 */
export interface CreateMemoDTO {
  readonly title: string;
  readonly content: string;
  readonly calendarId: string;
  readonly authorId: string;
  readonly color?: MemoColorRef;
  readonly isPinned?: boolean;
}

/**
 * Data for updating an existing memo
 */
export interface UpdateMemoDTO {
  readonly title?: string;
  readonly content?: string;
  readonly color?: MemoColorRef;
  readonly isPinned?: boolean;
}

// ============================================
// Type Guards
// ============================================

/**
 * Type guard for MemoColor
 */
export function isMemoColor(value: unknown): value is MemoColor {
  return (
    typeof value === 'string' && ALL_MEMO_COLORS.includes(value as MemoColor)
  );
}
